import React from 'react';
import PropTypes from 'prop-types';

// Provenance panel — shown when any QBO data was used in the snapshot. For
// each input field, indicates whether it came from QuickBooks, was edited
// after pre-fill, or was entered manually. CBVs use this to assess
// defensibility; owners use it for transparency.
//
// `dataSources` is server-derived (todo 125) — a typed
// `ValuationDataSourcesType` payload that the SmbValuationService stamps
// onto every result and persists on the snapshot's
// `results['data_sources']`. The legacy in-memory `inputs.qbo_source`
// shape is still accepted as a fallback so older saved snapshots that
// pre-date the server derivation continue to render their edit deltas.

function formatCurrencyFull(value) {
  if (!value && value !== 0) return '$0';
  return `$${Math.round(value).toLocaleString()}`;
}

function StatusDot({ variant = 'orange' }) {
  const colors = { orange: 'hsl(var(--succeedo-orange))', green: 'hsl(var(--chart-2))' };
  return (
    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: colors[variant] || colors.orange }} />
  );
}

StatusDot.propTypes = { variant: PropTypes.string };
StatusDot.defaultProps = { variant: 'orange' };

function DataSourcesPanel({ inputs, dataSources }) {
  // Server-derived (preferred) vs legacy in-memory provenance.
  const serverSources = dataSources || null;
  const legacySource = inputs?.qbo_source || null;
  const legacyFields = legacySource?.fields || {};

  const serverHasAnyQbo = !!serverSources && (
    serverSources.revenue === 'quickbooks'
    || serverSources.expenses === 'quickbooks'
    || serverSources.historical_revenues === 'quickbooks'
    || serverSources.historicalRevenues === 'quickbooks'
  );
  const legacyHasAnyQbo = Object.keys(legacyFields).length > 0 || (inputs?.cogs || 0) > 0;
  if (!serverHasAnyQbo && !legacyHasAnyQbo) return null;

  const adjustments = inputs?.adjustments || {};
  const rows = [];

  rows.push({
    key: 'revenue',
    label: 'Revenue',
    current: inputs?.revenue || 0,
    original: legacyFields.revenue,
    serverSource: serverSources?.revenue,
  });

  const currentOpex = inputs?.operating_expenses || inputs?.operatingExpenses || 0;
  const originalOpex = (legacyFields.cogs || 0) + (legacyFields.operatingExpenses || 0);
  rows.push({
    key: 'opex',
    label: 'Operating Expenses (incl. COGS)',
    current: currentOpex,
    original: originalOpex > 0 ? originalOpex : undefined,
    serverSource: serverSources?.expenses,
  });

  rows.push({
    key: 'owner_comp',
    label: 'Owner excess compensation',
    current: adjustments.owner_excess_comp || 0,
    original: legacyFields.ownerCompensation,
    // The server doesn't yet break out owner_compensation separately;
    // tag it QBO when the overall expenses came from QBO.
    serverSource: serverSources?.expenses,
  });

  rows.push({
    key: 'one_time',
    label: 'One-time expenses',
    current: adjustments.one_time_expenses || 0,
    original: legacyFields.oneTimeExpenses,
    serverSource: serverSources?.expenses,
  });

  const histSource = serverSources?.historical_revenues || serverSources?.historicalRevenues;
  if (Array.isArray(legacyFields.historicalRevenues)) {
    const hist = inputs?.historical_revenues || [];
    legacyFields.historicalRevenues.forEach((orig, i) => {
      rows.push({
        key: `hist_${i}`,
        label: `Year ${i + 1} revenue`,
        current: hist[i] || 0,
        original: orig,
        serverSource: histSource,
      });
    });
  } else if (histSource === 'quickbooks') {
    // Server says historical revenues came from QBO but we don't have the
    // original values to compute deltas — show rows tagged QuickBooks
    // without an edit-delta line.
    const hist = inputs?.historical_revenues || [];
    hist.forEach((current, i) => {
      rows.push({
        key: `hist_${i}`,
        label: `Year ${i + 1} revenue`,
        current,
        original: undefined,
        serverSource: 'quickbooks',
      });
    });
  }

  const syncedAtRaw = serverSources?.synced_at || serverSources?.syncedAt || legacySource?.syncedAt;
  const syncedAt = syncedAtRaw
    ? new Date(syncedAtRaw).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="bg-card border border-border rounded-[12px] p-6 mb-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <StatusDot variant="orange" />
          <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">Data Sources</span>
        </div>
        {syncedAt && (
          <span className="text-[11px] text-muted-foreground">QuickBooks synced {syncedAt}</span>
        )}
      </div>
      <div className="space-y-2.5">
        {rows.map((row) => {
          const hasOriginal = row.original !== undefined && row.original !== null && row.original > 0;
          const edited = hasOriginal && Math.round(row.current) !== Math.round(row.original);
          // Treat the row as QBO-sourced when the server says so OR when
          // we have an original value carried in legacy in-memory state.
          const treatAsQbo = row.serverSource === 'quickbooks' || hasOriginal;
          // eslint-disable-next-line no-nested-ternary
          const label = !treatAsQbo ? 'Manual' : (edited ? 'QuickBooks (edited)' : 'QuickBooks');
          const badgeStyle = !treatAsQbo
            ? { backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }
            : edited
              ? { backgroundColor: 'hsl(var(--succeedo-orange) / 0.15)', color: 'hsl(var(--succeedo-orange))', border: '1px solid hsl(var(--succeedo-orange) / 0.3)' }
              : { backgroundColor: 'hsl(var(--succeedo-orange) / 0.08)', color: 'hsl(var(--succeedo-orange))' };
          return (
            <div key={row.key} className="flex items-start justify-between gap-3 text-[13px]">
              <div className="flex-1 min-w-0">
                <div className="text-foreground">{row.label}</div>
                {edited && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Originally {formatCurrencyFull(row.original)} from QuickBooks
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-foreground font-medium tabular-nums">{formatCurrencyFull(row.current)}</span>
                <span className="font-mono text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full whitespace-nowrap" style={badgeStyle}>
                  {label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

DataSourcesPanel.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  inputs: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  dataSources: PropTypes.object,
};

DataSourcesPanel.defaultProps = {
  inputs: null,
  dataSources: null,
};

export default DataSourcesPanel;
